import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDownIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { HonduranFlag } from './shared/HonduranFlag';
export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    t
  } = useLanguage();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return <section id="home" ref={ref} className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-haa-navy" aria-label="Hero section" data-id="element-2458" data-mp="sd4b310cx6ny">
      {/* Parallax photo background */}
      <motion.div style={{
      y: photoY
    }} className="absolute inset-0 w-full h-[115%] -top-[7.5%]" data-id="element-2459" data-mp="381xn73sg3cct">
        {/* Honduran flag fills the frame behind the photo */}
        <HonduranFlag className="absolute inset-0 w-full h-full opacity-45" data-id="element-2460" data-mp="3o858h3cjnn0v" />
        <div className="absolute inset-0 bg-haa-navy/55" data-id="element-2461" data-mp="39acx8ne15ksh" />
        {/* Landscape hero image, fully visible at every width */}
        <img src="https://cdn.magicpatterns.com/uploads/iPHjEpGidZAqG1gMfpLmLH/1c7c57f7-be5c-44b0-8ce9-1416397b2473.jpg" alt="Person holding a blended Honduran and American flag facing the US Capitol" className="relative w-full h-full object-contain object-center" data-id="element-2462" data-mp="3e8ms0wvuejdk" />
      </motion.div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-haa-navy/80 via-haa-navy/40 to-haa-navy/20" data-id="element-2463" data-mp="2qi0l58xkr555" />
      <div className="absolute inset-0 bg-haa-blue/10" data-id="element-2464" data-mp="2vglpnkjv0aze" />

      {/* Content */}
      <motion.div style={{
      y,
      opacity
    }} className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto pt-24 lg:pt-28" data-id="element-2465" data-mp="2hjym2yxeimki">
        {/* Pre-title badge */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-2 mb-6 backdrop-blur-sm" data-id="element-2466" data-mp="s3undmopqq5v">
          <StarIcon className="w-4 h-4 text-haa-skyblue fill-haa-skyblue" data-id="element-2467" data-mp="1622jjsdxdnfz" />
          <span className="font-nunito text-white text-sm font-semibold tracking-widest uppercase" data-id="element-2468" data-mp="15zztx9gopwbs">
            {'Est. 2025'}
          </span>
          <StarIcon className="w-4 h-4 text-haa-skyblue fill-haa-skyblue" data-id="element-2469" data-mp="x1w6g7m52phc" />
        </motion.div>

        {/* Main Title */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.85
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.4
      }} className="flex justify-center mb-8 mt-4" data-id="element-2470" data-mp="3luia4t2z73mf">
          <div className="bg-white/50 rounded-2xl px-8 py-5 shadow-2xl backdrop-blur-sm" data-id="element-2471" data-mp="218jvhvsh6h3d">
            <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association Logo" className="w-64 sm:w-80 md:w-96 object-contain" data-id="element-2472" data-mp="267wjijv31u7j" />
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.6
      }} className="mb-10" data-id="element-2473" data-mp="2p0h7bmvxd94h">
          <p className="font-nunito italic text-white/90 text-lg sm:text-xl md:text-2xl font-600 mb-1 drop-shadow-lg" data-id="element-2474" data-mp="zlkl62mid1zh">
            {t('hero.connectTagline')}
          </p>
          <p className="font-nunito text-white/70 text-base sm:text-lg drop-shadow-md" data-id="element-2475" data-mp="49jf0ae80xor">
            {t('hero.diaspora')}
          </p>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2" data-id="element-2476" data-mp="e7h93eirhopa">
        <span className="font-nunito text-white/50 text-xs uppercase tracking-widest" data-id="element-2477" data-mp="3ct0lxfipafgb">
          {t('hero.scroll')}
        </span>
        <div className="animate-bounce-slow" data-id="element-2478" data-mp="3mpdgefm2kxc4">
          <ChevronDownIcon className="w-6 h-6 text-haa-skyblue" data-id="element-2479" data-mp="3molkf3ok8xlb" />
        </div>
      </motion.div>
    </section>;
}